import * as React from 'react';
import strings from '../../../../strings/strings.json';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import TextField from 'components/forms/TextField';
import useModifyRequest from 'hooks/useModifyRequest';
import useNeveForm from 'components/forms/NeveForm';
import { AlertBodyComponent } from 'components/alert/Alert';
import TextAreaField from 'components/forms/TextAreaField';
import useDeleteRequest from 'hooks/useDeleteRequest';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import useFormBlocker from 'components/forms/useFormBlocker';
import { EditModeSaveNotification } from '../activityForms/editActivityForms/shared/SharedEditTaskComponents';
import { WarningNotifierSlim } from 'components/notifiers/WarningNotifierSlim';
import SaveNotifier from 'components/notifiers/SaveNotifier';
import { FlexColMd, FlexEnd, FlexMd } from 'components/Elements';
import styled from 'styled-components';
import { sizes } from 'theme';
import { ErrorNotifierSlim } from 'components/notifiers/ErrorNotifierSlim';
import Button from 'components/buttons/Button';

type EditModuleFormProps = {
    id: string;
    title: string;
    description: string;
    closeFormAfterDeletion: () => void;
};

type EditModuleInput = {
    title: string;
    description: string;
};

type State = 'clean' | 'error' | 'saved';

export default function EditModuleForm({
    id,
    title,
    description,
    closeFormAfterDeletion,
}: EditModuleFormProps): JSX.Element {
    const course = React.useContext(BuildCourseContext).course!;
    const { setCourseState, editMode, refetchCourse } = React.useContext(BuildCourseContext);

    const [editModeSaved, setEditModeSaved] = React.useState<boolean>(false);
    const [deleteAlertOpen, setDeleteVerificationAlertOpen] = React.useState(false);
    const [state, setState] = React.useState<State>('clean');

    const { modifyData: updateModule, loading } = useModifyRequest(`modules/${id}`, 'PUT');
    const { deleteData: deleteModule, deleteInProgress } = useDeleteRequest(`modules/${id}`);
    const saving = loading || deleteInProgress;

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors, isDirty },
        reset,
    } = useNeveForm<EditModuleInput>({
        title: title,
        description: description,
    });

    useFormBlocker(isDirty, strings.courseEditorPage.unsavedChangesPrompt);

    async function onSubmit(formData: EditModuleInput) {
        setState('clean');

        if (editMode) {
            const updatedModules = course.modules.map((m) =>
                m.id === id ? { ...m, title: formData.title, description: formData.description } : m,
            );

            setCourseState((prev) => ({
                ...prev!,
                modules: updatedModules,
            }));

            reset(formData);
            setEditModeSaved(true);
            return;
        }

        const { errors } = await updateModule(formData);
        if (errors) {
            setState('error');
            return;
        }

        reset(formData);
        await refetchCourse();
        setState('saved');
    }

    const deleteModuleHandler = async () => {
        if (editMode) {
            setCourseState((prev) => {
                if (!prev) return prev;
                const filtered = prev.modules.filter((m) => m.id !== id);
                return { ...prev, modules: filtered };
            });
            closeFormAfterDeletion();
            return;
        }

        await deleteModule();
        await refetchCourse();
        closeFormAfterDeletion();
    };

    const courseOnlyHasOneModule = course.modules.length === 1;

    if (deleteAlertOpen) {
        return (
            <AlertBodyComponent
                alertText={strings.editModuleForm.alertText}
                closeAlert={() => setDeleteVerificationAlertOpen(false)}
                submitButtonTitle={strings.editModuleForm.alertButton}
                submitButtonAria={strings.editModuleForm.alertButtonAria}
                submitButtonOnClick={deleteModuleHandler}
            />
        );
    }

    return (
        <ModuleForm onSubmit={handleSubmit(onSubmit)}>
            <FlexColMd>
                <TextField
                    fieldName="moduleTitle"
                    labelText={strings.moduleForm.titleInputLabel}
                    inputAria={strings.moduleForm.titleInputAria}
                    maxLength={100}
                    errorMessage={errors.title?.message}
                    disabled={saving}
                    required
                    autoFocus
                    {...register('title', {
                        required: {
                            value: true,
                            message: requiredFieldErrorMessage(strings.moduleForm.titleInputLabel),
                        },
                        maxLength: {
                            value: 100,
                            message: createErrorMessage(strings.moduleForm.titleInputLabel, fieldErrorCodes.maxLength),
                        },
                    })}
                />
                <TextAreaField
                    fieldName={strings.moduleForm.descriptionLabel}
                    aria={strings.moduleForm.descriptionAria}
                    errorMessage={errors.description?.message}
                    maxLength={400}
                    charactersLeft={400 - watch('description').length}
                    {...register('description', {
                        maxLength: {
                            value: 400,
                            message: createErrorMessage(
                                strings.moduleForm.descriptionLabel,
                                fieldErrorCodes.maxLength,
                            ),
                        },
                    })}
                />

                {editMode && courseOnlyHasOneModule && (
                    <WarningNotifierSlim description={strings.editModeNotifiers.lastModuleInCourse} />
                )}

                <FlexEnd>
                    <FlexMd>
                        {state === 'error' && <ErrorNotifierSlim description="Error updating module." />}
                        <Button
                            title={strings.editModuleForm.removeModuleButton}
                            aria={strings.editModuleForm.removeModuleButtonAria}
                            colour={'BLACK'}
                            onClick={() => setDeleteVerificationAlertOpen(true)}
                            disabled={deleteInProgress || (editMode && courseOnlyHasOneModule)}
                        />
                        <Button
                            title={strings.moduleForm.submitButton}
                            aria={strings.moduleForm.submitButtonAria}
                            disabled={saving || (editMode && !isDirty)}
                            submitting={saving}
                            onClick={handleSubmit(onSubmit)}
                            colour='PRIMARY'
                        />
                    </FlexMd>
                </FlexEnd>

                {state === 'saved' && !isDirty && (
                    <FlexEnd>
                        <SaveNotifier />
                    </FlexEnd>
                )}

                {editModeSaved && !isDirty && (
                    <FlexEnd>
                        <EditModeSaveNotification />
                    </FlexEnd>
                )}
            </FlexColMd>
        </ModuleForm>
    );
}

const ModuleForm = styled.form`
    padding: 0 ${sizes.spacingSm};
`;
